import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame } from "@phosphor-icons/react";
import api from "@/lib/api";

const timeAgo = (iso) => {
  if (!iso) return "";
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 3600) return "just now";
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  const days = Math.floor(seconds / 86400);
  return days === 1 ? "yesterday" : `${days} days ago`;
};

export const DonorWall = () => {
  const [donors, setDonors] = useState(null);

  useEffect(() => {
    api.get("/donations/recent").then((r) => setDonors(r.data)).catch(() => setDonors([]));
  }, []);

  if (donors === null) return null;

  return (
    <section className="py-16 lg:py-20 border-t border-forest/15 bg-bonealt/40" data-testid="donor-wall">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <Flame size={22} weight="fill" className="text-terra" />
          <h2 className="font-serif font-bold tracking-tight text-forest text-2xl lg:text-3xl" data-testid="donor-wall-heading">
            Recent lamps lit
          </h2>
        </motion.div>

        {donors.length === 0 ? (
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-serif italic text-lg text-moss"
            data-testid="donor-wall-empty"
          >
            The next lamp could be lit by you.{" "}
            <a href="#get-involved" className="text-terra underline underline-offset-4 hover:text-forest transition-colors" data-testid="donor-wall-cta">
              Light one
            </a>
          </motion.p>
        ) : (
          <div className="flex flex-wrap gap-3" data-testid="donor-wall-list">
            {donors.map((d, i) => (
              <motion.div
                key={`${d.display_name}-${d.at}-${i}`}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="flex items-center gap-3 border border-forest/15 bg-bone px-4 py-3"
                data-testid={`donor-chip-${i}`}
              >
                <Flame size={16} weight="fill" className="text-terra shrink-0" />
                <p className="text-sm text-ink">
                  <span className="font-bold text-forest">{d.display_name}</span>
                  {" lit "}
                  <span className="font-serif font-bold text-terra">
                    {d.currency === "INR" ? "₹" : "$"}{Number(d.amount).toLocaleString("en-IN")}
                  </span>
                  {d.frequency === "monthly" && <span className="text-moss"> every month</span>}
                  {d.at && <span className="text-moss"> · {timeAgo(d.at)}</span>}
                </p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
